import { ArrowLeft20, Close20, Search16 } from '@carbon/icons-react';
import { isNaN } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { ProjectFileGroup } from 'api/project-files/types';
import { RoutePaths } from 'constant';
import { useIsTablet } from 'hooks/use-media-query';
import { useTranslate } from 'hooks/use-translation';
import { handleDownload } from 'utils/attachment';
import { isImageUrlFromExtension } from 'utils/files';
import { SearchInput } from '../../components/cp-input';
import { useGetBackground } from '../../types/utils';
import { CpWidgetProps } from '../../types/widget';
import { useCustomerPortalHashMap } from '../../utils';
import { BackIconWrapper, BackWrapper } from '../chat/components';
import { CpSectionWrapper } from '../common';
import { CpWidgetHeader } from '../common/header';
import { CpAllFilesForLaptop } from './cp-all-files.laptop';
import { CpAllFilesForMobile } from './cp-all-files.mobile';
import {
  ListOrGridMode,
  SPOTLIGHT_FOLDER_ID,
  RECENTS_FOLDER_ID,
  GENERAL_SHARED_FILES_FOLDER_ID,
  PROJECT_FILES_FOLDER_ID,
  UrlType,
  CpAllFilesData,
} from './types';

export const StyledBackWrapper = styled(BackWrapper)`
  padding: 0;
  margin-bottom: var(--cp-spacing-07);
  display: flex;
  align-items: center;
  ${CpWidgetHeader.Title} {
    margin-bottom: 0;
    /* ellipsis */
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 90%;
  }
  ${BackIconWrapper} {
    height: 42px;
    width: 32px;
    display: flex;
    align-items: center;
    justify-content: start;
    padding: 0;
    margin-right: 0;
    @media (min-width: 481px) {
      margin-top: var(--cp-spacing-03);
    }

    @media (max-width: 480px) {
      margin-top: var(--cp-spacing-02);
    }
  }
`;

export const CpAllFiles: React.FC<CpWidgetProps> = ({
  widget,
  data: _data,
  projectId,
  disabled,
  onRuntimeConfigChange,
}) => {
  const router = useHistory();
  const isResponsive = useIsTablet();
  const background = useGetBackground(widget);
  const data = useMemo(() => _data as CpAllFilesData, [_data]);
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<ListOrGridMode>(ListOrGridMode.Grid);
  const { hashMap, setHashMap, removeKey } = useCustomerPortalHashMap();
  const translate = useTranslate();
  const _currentFolder: ProjectFileGroup | undefined = useMemo(() => {
    const isCurrentFolderANumber = !isNaN(Number(hashMap?.folder));

    if (isCurrentFolderANumber) {
      const folderId = Number(hashMap?.folder);

      return data?.folders.find((folder) => folder.folderId === folderId);
    }
    switch (hashMap?.folder) {
      case 'spotlight':
        return {
          folderId: SPOTLIGHT_FOLDER_ID,
          folderName: translate('spotlight'),
          attachments: data?.spotlightedAttachments,
          accountId: -1,
          createdAt: -1,
          createdBy: -1,
          isDefault: true,
          projectId,
          isPrivate: false,
        } as ProjectFileGroup;
      case 'recents':
        return {
          folderId: RECENTS_FOLDER_ID,
          folderName: translate('recents'),
          attachments: [],
          accountId: -1,
          createdAt: -1,
          createdBy: -1,
          isDefault: true,
          projectId,
          isPrivate: false,
        } as ProjectFileGroup;

      case 'general_shared_files':
        return {
          folderId: GENERAL_SHARED_FILES_FOLDER_ID,
          folderName: translate('project.files.shared.group.title'),
          attachments: data?.generalSharedAttachments,
          accountId: -1,
          createdAt: -1,
          createdBy: -1,
          isDefault: true,
          projectId,
          isPrivate: false,
        } as ProjectFileGroup;

      case 'project_files':
        return {
          folderId: PROJECT_FILES_FOLDER_ID,
          folderName: translate('project.task.attachments.title', {
            count: data?.attachmentsInProject?.length ?? 0,
          }),
          attachments: [],
          accountId: -1,
          createdAt: -1,
          createdBy: -1,
          isDefault: true,
          projectId,
          isPrivate: false,
        } as ProjectFileGroup;

      default:
        if (!isResponsive) {
          return {
            folderId: PROJECT_FILES_FOLDER_ID,
            folderName: translate('project.task.attachments.title', {
              count: data?.attachmentsInProject?.length ?? 0,
            }),
            attachments: [],
            accountId: -1,
            createdAt: -1,
            createdBy: -1,
            isDefault: true,
            projectId,
            isPrivate: false,
          } as ProjectFileGroup;
        }

        return undefined;
    }
  }, [
    data?.attachmentsInProject?.length,
    data?.folders,
    data?.generalSharedAttachments,
    data?.spotlightedAttachments,
    hashMap?.folder,
    isResponsive,
    projectId,
    translate,
  ]);

  const currentFolder = useMemo(() => {
    if (_currentFolder) {
      return {
        ..._currentFolder,
        folderName: search?.trim()
          ? translate('search_results')
          : _currentFolder.folderName,
      };
    }

    return _currentFolder;
  }, [_currentFolder, search, translate]);

  const onFolderClick = useCallback(
    (folderId: UrlType | undefined) => {
      if (!folderId) {
        removeKey('folder');
        setSearch('');

        return;
      }
      setHashMap('folder', folderId.toString());
    },
    [removeKey, setHashMap],
  );

  useEffect(() => {
    if (!hashMap.folder && !isResponsive) {
      setHashMap('folder', 'project_files');
    }
  }, [hashMap.folder, isResponsive, setHashMap]);

  const allAttachments = useMemo(() => {
    return (data?.folders?.flatMap((f) => f.attachments) ?? [])?.concat(
      data?.attachmentsInProject?.map((a) => a?.attachment) ?? [],
    );
  }, [data?.attachmentsInProject, data?.folders]);

  const currentAttachments = useMemo(() => {
    if (search?.trim()) {
      return allAttachments?.filter((e) =>
        e?.name?.toLowerCase?.()?.includes(search?.trim?.().toLowerCase?.()),
      );
    }
    if (currentFolder?.folderId === PROJECT_FILES_FOLDER_ID) {
      const attachmentsInProject = data?.attachmentsInProject ?? [];

      if (mode === ListOrGridMode.Grid) {
        const atts = attachmentsInProject.map((a) => a?.attachment);

        return atts;
      }

      return attachmentsInProject;
    }
    if (currentFolder?.folderId === RECENTS_FOLDER_ID) {
      const attachmentsInProject = data?.recents ?? [];

      if (mode === ListOrGridMode.Grid) {
        const atts = attachmentsInProject.map((a) => a?.attachment);

        return atts;
      }

      return attachmentsInProject;
    }

    if (currentFolder?.folderId === SPOTLIGHT_FOLDER_ID) {
      if (mode === ListOrGridMode.Grid) {
        return data?.spotlightedAttachments;
      }

      return [];
    }

    const attachments = currentFolder?.attachments;

    return (
      attachments?.filter((e) =>
        e?.name?.toLowerCase?.()?.includes(search?.trim?.().toLowerCase?.()),
      ) ?? []
    );
  }, [
    allAttachments,
    currentFolder?.attachments,
    currentFolder?.folderId,
    data?.attachmentsInProject,
    data?.recents,
    data?.spotlightedAttachments,
    mode,
    search,
  ]);

  const onFileClick = useCallback(
    (attachmentId: number) => {
      const attachment = allAttachments?.find(
        (a) => a?.attachmentId === attachmentId,
      );

      if (!attachment) {
        return;
      }

      if (isImageUrlFromExtension(attachment?.name)) {
        router.push(
          RoutePaths.customerPortalAttachmentById
            .replace(':projectId', projectId.toString())
            .replace(':attachmentId', attachmentId.toString()),
        );
      } else {
        // open in a new tab
        window.open(attachment?.location, '_blank');
      }
    },
    [allAttachments, projectId, router],
  );

  return (
    <CpSectionWrapper
      background={background}
      paddingY={
        widget?.widgetDefinition?.styles?.paddingY as number | undefined
      }
      isDisabled={disabled}
    >
      {isResponsive && currentFolder ? (
        <>
          <StyledBackWrapper>
            <BackIconWrapper
              data-cy="cp.files.back"
              onClick={() => onFolderClick(undefined)}
            >
              <ArrowLeft20 />
            </BackIconWrapper>
            <CpWidgetHeader.Title data-cy="cp.files.title" autoAdjustColor>
              {currentFolder?.folderName}
            </CpWidgetHeader.Title>
          </StyledBackWrapper>
        </>
      ) : (
        <CpWidgetHeader>
          <CpWidgetHeader.Title data-cy="cp.files.title" autoAdjustColor>
            {data.title}
          </CpWidgetHeader.Title>
          <CpWidgetHeader.Description
            data-cy="cp.files.description"
            autoAdjustColor
          >
            {data.description}
          </CpWidgetHeader.Description>
        </CpWidgetHeader>
      )}
      {!isResponsive || currentFolder ? (
        <SearchInput
          data-cy="cp.files.search"
          prefix={<Search16 />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={translate('search')}
          suffix={
            search ? <Close20 onClick={() => setSearch('')} /> : undefined
          }
        />
      ) : null}
      {isResponsive ? (
        <CpAllFilesForMobile
          refetch={() => onRuntimeConfigChange?.({})}
          handleDownload={handleDownload}
          currentAttachments={currentAttachments}
          currentFolder={currentFolder}
          data={data}
          mode={mode}
          onFolderClick={onFolderClick}
          search={search}
          setMode={setMode}
          onFileClick={onFileClick}
          projectId={projectId}
        />
      ) : (
        <CpAllFilesForLaptop
          refetch={() => onRuntimeConfigChange?.({})}
          handleDownload={handleDownload}
          currentAttachments={currentAttachments}
          currentFolder={currentFolder}
          data={data}
          mode={mode}
          onFolderClick={onFolderClick}
          search={search}
          setMode={setMode}
          onFileClick={onFileClick}
          projectId={projectId}
        />
      )}
    </CpSectionWrapper>
  );
};
